import React, { useState, memo } from "react";
import crossIconForChip from "../../assets/icons/filterChip_cancel_icon.svg";
import "./FilterChips.css";
import resetIcon from "../../assets/icons/resetIcon.svg";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { FaInfoCircle } from "react-icons/fa";
import { FILTER_CHIPS_MAPPER } from "../../constant/filterChipsData";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Popover,
  Typography,
} from "@mui/material";

function FilterChips({
  filterChipList,
  onCancelClickRemoveFilteringHandler,
  onResetButtonClick,
  isResetButtonNeeded,
  pageName,
  enableViewAll = true,
  maxVisibleValues = 2,
  viewAllMode = "popover",
  onViewAll,
}) {
  const [popoverState, setPopoverState] = useState(null);
  const [modalState, setModalState] = useState(null);

  const FIELD_NAME_MAPPER = FILTER_CHIPS_MAPPER[pageName] ?? {};

  const getFieldLabel = (fieldNameOfSelectedBtn) =>
    FIELD_NAME_MAPPER[fieldNameOfSelectedBtn?.toLowerCase()] ??
    fieldNameOfSelectedBtn;

  const onCancelClickHandler = (e, fieldNameForAPIFilter) => {
    onCancelClickRemoveFilteringHandler?.(fieldNameForAPIFilter);
  };

  const openViewAll = (event, listData) => {
    event?.stopPropagation?.();
    if (!enableViewAll) return;

    if (typeof onViewAll === "function") {
      onViewAll(listData?.checkedValue ?? [], listData);
      return;
    }

    const fieldLabel = getFieldLabel(listData?.fieldNameOfSelectedBtn);
    const payload = {
      fieldName: fieldLabel,
      values: listData.checkedValue,
    };

    if (viewAllMode === "modal") {
      setModalState(payload);
      return;
    }

    setPopoverState({
      ...payload,
      anchorEl: event.currentTarget,
    });
  };

  const closePopover = () => setPopoverState(null);
  const closeModal = () => setModalState(null);

  const renderValuesList = (values = []) => (
    <ul className="filter-chip-view-all-list">
      {values.map((val, index) => (
        <li key={`${val}-${index}`} className="filter-chip-view-all-list-item">
          {val}
        </li>
      ))}
    </ul>
  );

  const renderCheckedValues = (listData) => {
    const { checkedValue } = listData;

    if (!checkedValue || checkedValue.length === 0) {
      return null;
    }

    const visibleValues = checkedValue.slice(0, maxVisibleValues);
    const hiddenCount = checkedValue.length - maxVisibleValues;
    const hasMore = hiddenCount > 0;

    return (
      <>
        <span className="chip-box-visible-values">
          {visibleValues.map((val, index) => (
            <span key={index}>
              {`${val}${index < visibleValues.length - 1 ? "," : ""} `}
            </span>
          ))}
        </span>
        {hasMore && enableViewAll ? (
          <span className="chip-box-extra-actions">
            <button
              type="button"
              className="filter-chip-count-btn show-all-checked-filtering"
              title="View all selected"
              onClick={(e) => openViewAll(e, listData)}
            >
              <span className="above-two-chip-data-value-container">
                {` + ${hiddenCount}`}
              </span>
            </button>
            <button
              type="button"
              className="filter-chip-view-all-btn"
              title="View all"
              onClick={(e) => openViewAll(e, listData)}
            >
              View all
            </button>
          </span>
        ) : null}
      </>
    );
  };

  return (
    <>
      <div className="main-container-for-chips filter-chips-root">
        {filterChipList?.length
          ? filterChipList.map((listData, i) => {
              const chipData = { ...listData };
              if (chipData?.fieldNameOfSelectedBtn === "Objective") {
                chipData.checkedValue = chipData?.checkedValue?.map((data) =>
                  data === "Retarget" ? "Retargeting" : data
                );
              }

              return (
                <div className="d-flex" key={`${chipData.field_name}-${i}`}>
                  {chipData?.tooltip === true ? (
                    <ReactTooltip
                      id="metric-filter-chip"
                      style={{
                        zIndex: "999999",
                        fontSize: "12px",
                        padding: "8px",
                      }}
                      effect="solid"
                      place="top"
                      variant="dark"
                    />
                  ) : null}
                  <div className="d-flex justify-content-between chip-box-conrainer">
                    <div className="d-flex align-items-center">
                      <span className="chip-box-field-name-text">
                        {`${getFieldLabel(chipData?.fieldNameOfSelectedBtn)}:`}
                      </span>
                      {chipData && Array.isArray(chipData?.checkedValue) ? (
                        <span className="chip-box-field-name-selected-opt-text">
                          {chipData?.checkedValue?.length
                            ? renderCheckedValues(chipData)
                            : null}
                        </span>
                      ) : (
                        <span className="chip-box-field-name-selected-opt-text">
                          {chipData.checkedValue !== "" &&
                          chipData?.minValue === "-Infinity"
                            ? ` ${chipData.minValue} to ${chipData.maxValue}`
                            : ` ${chipData.minValue} - ${chipData.maxValue}`}
                        </span>
                      )}
                    </div>
                    {chipData?.tooltip ? (
                      <div
                        className="px-2 d-flex align-items-center"
                        data-tooltip-id={
                          chipData?.tooltip ? "metric-filter-chip" : true
                        }
                        data-tooltip-html={`${chipData?.toolTipContent}`}
                      >
                        <FaInfoCircle className="filter-chip-info-icon" size={14} />
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="chip-box-close-btn"
                      onClick={(e) =>
                        onCancelClickHandler(e, chipData.field_name)
                      }
                      aria-label="Remove filter"
                    >
                      <img src={crossIconForChip} alt="Remove filter" />
                    </button>
                  </div>
                </div>
              );
            })
          : null}
        <div
          className={
            isResetButtonNeeded && filterChipList?.length
              ? "mx-3 align-items-center mb-2"
              : "hide"
          }
        >
          <button
            className="primary-inactive-btn reset-btn-for-filter-btn"
            onClick={onResetButtonClick}
          >
            <span>
              <img className="reset-icon-img" src={resetIcon} alt={"reset"}></img>
            </span>
            <span className="text-color-for-reset-btn">Reset</span>
          </button>
        </div>
      </div>

      <Popover
        open={Boolean(popoverState?.anchorEl)}
        anchorEl={popoverState?.anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            className: "filter-chip-view-all-popover",
            sx: { zIndex: 9999 },
          },
        }}
      >
        <Box className="filter-chip-view-all-popover-content">
          <Typography className="filter-chip-view-all-title" variant="subtitle2">
            {popoverState?.fieldName}
            {popoverState?.values?.length
              ? ` (${popoverState.values.length})`
              : ""}
          </Typography>
          <Box sx={{ px: 0, py: 0 }}>{renderValuesList(popoverState?.values)}</Box>
        </Box>
      </Popover>

      <Dialog
        open={Boolean(modalState)}
        onClose={closeModal}
        maxWidth="sm"
        fullWidth
        className="filter-chip-view-all-dialog"
      >
        <DialogTitle className="filter-chip-view-all-dialog-title">
          {modalState?.fieldName}
          {modalState?.values?.length ? ` (${modalState.values.length})` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {renderValuesList(modalState?.values)}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default memo(FilterChips);
